import { emitApiMetricSample } from "./metric-emitter";
import { resolveMatchingApiMonitorsByOperation } from "./helpers";
import type {
  ApiMonitorDefinition,
  ApiRequestContext,
  MonitoringPrimitive,
  PodMonitoringSchema,
} from "./types";

// In-app mobile bridge instrumentation. The connection-manager vendor library
// routes requests through a synchronous `prompt('callAPI://<json>')` call when
// running inside the native app. Each bridge call carries an `operation` id but
// no request URL, so monitors are matched by operationId instead of URL patterns.
const CALL_API_PROTOCOL = "callAPI://";

// Stores the original prompt so multiple installers share one patched wrapper.
interface PromptMonitorGlobals {
  __bflMonitoringOriginalPrompt__?: typeof window.prompt;
  __bflMonitoringPromptRefCount__?: number;
}

// Captures the metric ids and request attributes needed after a bridge call.
interface MonitoredApiRequest {
  errorMetricId: string;
  responseTimeMetricId: string;
  attributes: Record<string, MonitoringPrimitive>;
}

interface PromptOutcome {
  success: boolean;
  statusCode: number;
  message: string | null;
}

// Builds the classifier input object for the current bridge call.
function createApiRequestContext(operationId: string, payload: unknown): ApiRequestContext {
  return {
    url: `cm://${operationId}`,
    method: "POST",
    input: typeof payload === "string" ? payload : String(payload ?? ""),
    init: undefined,
    pagePath: typeof window === "undefined" ? "" : window.location.pathname,
    pageUrl: typeof window === "undefined" ? "" : window.location.href,
  };
}

// Precomputes the metric ids and attributes for one matched API monitor.
function createMonitorSample(
  monitor: ApiMonitorDefinition,
  requestContext: ApiRequestContext
): MonitoredApiRequest {
  const classification = monitor.classifyRequest?.(requestContext);

  return {
    errorMetricId: monitor.errorMetricId,
    responseTimeMetricId: monitor.responseTimeMetricId,
    attributes: {
      api_name: classification?.apiName ?? monitor.apiName,
      request_transport: "prompt",
      request_method: requestContext.method,
      request_page_path: requestContext.pagePath,
      request_page_url: requestContext.pageUrl,
      ...(classification?.attributes ?? {}),
    },
  };
}

// Derives a success signal and best-effort status code from the bridge result.
function parsePromptResult(result: string | null): PromptOutcome {
  if (result === null) {
    return { success: false, statusCode: 0, message: null };
  }

  try {
    const parsed = JSON.parse(result);

    if (parsed && typeof parsed === "object") {
      const statusValue = parsed.STATUS ?? parsed.status;
      const rawStatusCode = parsed.STATUS_CODE ?? parsed.statusCode ?? parsed.status_code;
      let statusCode: number;

      if (typeof rawStatusCode === "number") {
        statusCode = rawStatusCode;
      } else if (typeof rawStatusCode === "string") {
        statusCode = Number(rawStatusCode) || 0;
      } else {
        statusCode = 0;
      }
      const success =
        String(statusValue).toUpperCase() === "S" || (statusCode >= 200 && statusCode < 300);

      return {
        success,
        statusCode,
        message: typeof parsed.description === "string" ? parsed.description : null,
      };
    }
  } catch {
    // Non-JSON responses (e.g. "Api Name: <operation> not found") are failures.
  }

  return { success: false, statusCode: 0, message: result };
}

// Records a completed bridge call outcome.
function recordPromptOutcome(
  schema: PodMonitoringSchema,
  monitoredRequests: MonitoredApiRequest[],
  operationId: string,
  duration: number,
  outcome: PromptOutcome
): void {
  monitoredRequests.forEach((monitor) => {
    emitApiMetricSample(schema, monitor.responseTimeMetricId, {
      value: duration,
      unit: "ms",
      statusCode: outcome.statusCode,
      success: outcome.success,
      percentile: 90,
      extra: {
        ...monitor.attributes,
        request_operation: operationId,
        ...(outcome.message ? { error_message: outcome.message } : {}),
      },
    });

    emitApiMetricSample(schema, monitor.errorMetricId, {
      value: 1,
      unit: "request",
      statusCode: outcome.statusCode,
      success: outcome.success,
      extra: {
        ...monitor.attributes,
        request_operation: operationId,
        is_error: !outcome.success,
        ...(outcome.message ? { error_message: outcome.message } : {}),
      },
    });
  });
}

// Patches window.prompt so matching `callAPI://` bridge calls emit API metrics.
export function installPromptMonitoring(schema: PodMonitoringSchema): () => void {
  if (typeof window === "undefined" || typeof window.prompt !== "function") {
    return () => undefined;
  }

  const globals = window as Window & PromptMonitorGlobals;
  const originalPrompt = globals.__bflMonitoringOriginalPrompt__ ?? window.prompt.bind(window);

  if (!globals.__bflMonitoringOriginalPrompt__) {
    globals.__bflMonitoringOriginalPrompt__ = originalPrompt;
    globals.__bflMonitoringPromptRefCount__ = 0;
  }

  globals.__bflMonitoringPromptRefCount__ = (globals.__bflMonitoringPromptRefCount__ ?? 0) + 1;

  window.prompt = function monitoredPrompt(
    this: Window,
    message?: string,
    defaultValue?: string
  ): string | null {
    const startTime = performance.now();
    const result = originalPrompt.call(this, message, defaultValue);

    if (typeof message === "string" && message.startsWith(CALL_API_PROTOCOL)) {
      const payloadRaw = message.slice(CALL_API_PROTOCOL.length);
      let payload: unknown = null;

      try {
        payload = JSON.parse(payloadRaw);
      } catch {
        payload = payloadRaw;
      }

      const operationId = (payload as { operation?: unknown } | null)?.operation;

      if (typeof operationId === "string") {
        const matchingMonitors = resolveMatchingApiMonitorsByOperation(schema, operationId);

        if (matchingMonitors.length > 0) {
          const duration = performance.now() - startTime;
          const requestContext = createApiRequestContext(operationId, payload);
          const monitoredRequests = matchingMonitors.map((monitor) =>
            createMonitorSample(monitor, requestContext)
          );

          recordPromptOutcome(
            schema,
            monitoredRequests,
            operationId,
            duration,
            parsePromptResult(result)
          );
        }
      }
    }

    return result;
  } as typeof window.prompt;

  return () => {
    const currentRefCount = (globals.__bflMonitoringPromptRefCount__ ?? 1) - 1;
    globals.__bflMonitoringPromptRefCount__ = Math.max(currentRefCount, 0);

    if (globals.__bflMonitoringPromptRefCount__ === 0 && globals.__bflMonitoringOriginalPrompt__) {
      window.prompt = globals.__bflMonitoringOriginalPrompt__;
      delete globals.__bflMonitoringOriginalPrompt__;
      delete globals.__bflMonitoringPromptRefCount__;
    }
  };
}
